import { SquarePen, Trash2 } from "lucide-react";
import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { EditExpenceModal } from "./EditExpenceModal";
import api from "../../lib/api";
import Loader from "../ui/Loader";
import { useDeleteExpense } from "../../hooks/useExpenceApi";
import { useAuth } from "../../hooks/AuthContext";
import Pagination from "../Models/Pagination";
import DescriptionCell from "../Models/DescriptionCell";

const getStatusBadge = (status) => {
  switch (status) {
    case "approve":
      return "bg-green-200 text-green-800";
    case "reject":
      return "bg-red-200 text-red-800";
      case "pending":
        return "bg-gray-200 text-white-800";
    default:
      return "bg-gray-200 text-gray-800";
  }
};



const ExpensesList = ({ expenses, isLoading, statusFilter, paginationData, onPageChange }) => {
  const [editingExpense, setEditingExpense] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const { mutate: deleteExpense } = useDeleteExpense();

  // Local state to manage expenses instantly
  const [localExpenses, setLocalExpenses] = useState(expenses || []);

  const { user } = useAuth();

  // Sync local state whenever props change
  useEffect(() => {
    if (statusFilter?.length > 0) {
      const filtered = expenses.filter((exp) => statusFilter.includes(exp.status));
      setLocalExpenses(filtered);
    } else {
      setLocalExpenses(expenses || []);
    }
  }, [expenses, statusFilter]);

  // Fetch all employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoadingEmployees(true);
        const res = await api.get("/employees");
        setEmployees(res.data.data.employees);
      } catch (err) {
        console.error("Error fetching employees:", err);
      } finally {
        setLoadingEmployees(false);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    document.body.style.overflow = editingExpense ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [editingExpense]);

  const handleDelete = (id) => {
    Swal.fire({
      title: "Delete this expense?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
    }).then((res) => {
      if (res.isConfirmed) {
        deleteExpense(id, {
          onSuccess: () => {
            Swal.fire({
              title: "Deleted!",
              icon: "success",
              timer: 1000,
              showConfirmButton: false,
            });
            setLocalExpenses((prev) => prev.filter((e) => e._id !== id));
          },
          onError: (error) => {
            Swal.fire({
              title: "Failed to delete",
              text: error.message || "Something went wrong",
              icon: "error",
            });
          },
        });
      }
    });
  };

  if (isLoading) return <Loader />;

  if (!localExpenses || localExpenses.length === 0)
    return <p className="text-gray-500 mt-4 text-center">No expenses found.</p>;

  return (
    <div className="rounded-lg flex flex-col pl-3 gap-4 w-full">
      <div>
        <p className="text-base font-semibold mt-6">Expenses Data</p>
      </div>
      <div className="overflow-x-auto rounded-lg shadow border">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase">Employee</th>
              <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase">category</th>

              <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase">Shop Name</th>
              <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left font-medium text-gray-500 uppercase">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {localExpenses.map((exp) => (
              <tr key={exp._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-gray-800">{exp.employeeName}</td>
                <DescriptionCell description={exp.description} />
                <td className="px-6 py-4 text-gray-800">{exp.categoryName}</td>

                <td className="px-6 py-4 text-gray-800">{exp.shopName}</td>

                <td className="px-6 py-4 text-gray-800">₹{exp.amount}</td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(exp.status)}`}>
                    {exp.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-800">
                  {new Date(exp.date * 1000).toLocaleDateString()}
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-3">
                    <SquarePen
                      className="text-green-500 cursor-pointer hover:text-green-600"
                      onClick={() => setEditingExpense(exp)}
                    />
                    {user?.role !== "employee" && (
                      <Trash2
                        className="text-red-500 cursor-pointer hover:text-red-600"
                        onClick={() => handleDelete(exp._id)}
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={paginationData?.page || 1}
        totalPages={paginationData?.pages || 1}
        onPageChange={onPageChange}
        maxVisible={3}
      />


      {/* Edit Expense Modal */}
      {editingExpense && (
        <EditExpenceModal
          expense={editingExpense}
          employees={employees}
          loadingEmployees={loadingEmployees}
          onClose={() => setEditingExpense(null)}
        />
      )}
    </div>
  );
};

export default ExpensesList;
